import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, Settings, CreditCard, Key, Users, 
  RefreshCw, Save, ArrowLeft, Check, Copy,
  DollarSign, AlertTriangle, Trophy
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const OrganizationSettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orgData, setOrgData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  
  // Form states
  const [orgName, setOrgName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [billingPath, setBillingPath] = useState('athlete_funded');

  useEffect(() => {
    if (user?.org_id) {
      fetchOrganization();
    }
  }, [user]);

  const fetchOrganization = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/organizations/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const org = response.data;
      setOrgData(org);
      setOrgName(org.name || '');
      setContactEmail(org.contact_email || '');
      setContactPhone(org.contact_phone || '');
      setBillingPath(org.billing_path || 'athlete_funded');
    } catch (error) {
      toast.error('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('aura_token');
      await axios.put(`${API}/organizations/${user.org_id}`, {
        name: orgName,
        contact_email: contactEmail,
        contact_phone: contactPhone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Organization settings saved!');
      fetchOrganization();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeBillingPath = async (newPath) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.put(`${API}/organizations/${user.org_id}/billing-path`, {
        billing_path: newPath
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingPath(newPath);
      toast.success(`Switched to ${newPath === 'org_funded' ? 'Organization-Funded' : 'Athlete-Funded'} plan`);
      fetchOrganization();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change billing path');
    }
  };

  const handleRegenerateInviteCode = async () => {
    if (!confirm('This will invalidate the current invite code. Athletes who haven\'t joined yet will need the new code. Continue?')) {
      return;
    }
    
    setRegenerating(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/organizations/${user.org_id}/regenerate-code`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgData(prev => ({ ...prev, invite_code: response.data.invite_code }));
      toast.success('New invite code generated!');
    } catch (error) {
      toast.error('Failed to regenerate invite code');
    } finally {
      setRegenerating(false);
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(orgData?.invite_code || '');
    setCopied(true);
    toast.success('Invite code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-aura-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-aura-secondary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-aura-bg/95 backdrop-blur-lg border-b border-white/5">
        <div className="flex items-center justify-between h-14 px-4 max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/organization')}
              className="text-aura-text-muted hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-aura-secondary" />
              <span className="font-heading font-bold text-lg text-white">Organization Settings</span>
            </div>
          </div>
        </div>
      </header>

      <main className="px-4 py-6 max-w-4xl mx-auto space-y-6">
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="w-full bg-aura-surface border border-white/10 mb-6">
            <TabsTrigger value="general" className="flex-1 data-[state=active]:bg-aura-primary">
              <Building2 className="w-4 h-4 mr-2" />
              General
            </TabsTrigger>
            <TabsTrigger value="billing" className="flex-1 data-[state=active]:bg-aura-primary">
              <CreditCard className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="invites" className="flex-1 data-[state=active]:bg-aura-primary">
              <Key className="w-4 h-4 mr-2" />
              Invites
            </TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-6">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
                  Organization Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Organization Name
                  </Label>
                  <Input
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="Enter organization name"
                    className="h-12 bg-aura-bg border-white/10 text-white"
                    data-testid="org-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Contact Email
                  </Label>
                  <Input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="contact@organization.com"
                    className="h-12 bg-aura-bg border-white/10 text-white"
                    data-testid="org-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                    Contact Phone (Optional)
                  </Label>
                  <Input
                    type="tel"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="h-12 bg-aura-bg border-white/10 text-white"
                    data-testid="org-phone-input"
                  />
                </div>

                <div className="pt-4">
                  <Button
                    onClick={handleSaveGeneral}
                    disabled={saving}
                    className="bg-aura-secondary hover:bg-aura-secondary/90 text-white"
                    data-testid="save-org-settings-btn"
                  >
                    {saving ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Organization Stats */}
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
                  Organization Stats
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-white/5 rounded-lg">
                    <Users className="w-6 h-6 text-aura-secondary mx-auto mb-2" />
                    <p className="font-heading font-bold text-2xl text-white">
                      {orgData?.athlete_count || 0}
                    </p>
                    <p className="text-xs text-aura-text-muted">Athletes</p>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-lg">
                    <Trophy className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                    <p className="font-heading font-bold text-2xl text-white capitalize">
                      {orgData?.org_type || 'team'}
                    </p>
                    <p className="text-xs text-aura-text-muted">Type</p>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-lg">
                    <CreditCard className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <p className="font-heading font-bold text-2xl text-white capitalize">
                      {billingPath === 'org_funded' ? 'Org' : 'Athlete'}
                    </p>
                    <p className="text-xs text-aura-text-muted">Billing</p>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-lg">
                    <Key className="w-6 h-6 text-aura-primary mx-auto mb-2" />
                    <p className="font-heading font-bold text-lg text-white font-mono">
                      {orgData?.invite_code}
                    </p>
                    <p className="text-xs text-aura-text-muted">Invite Code</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Settings */}
          <TabsContent value="billing" className="space-y-6">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
                  Billing Path
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-aura-text-muted">
                  Choose how your organization pays for Aura. You can switch paths at any time.
                </p>

                {/* Org-Funded Option */}
                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    billingPath === 'org_funded' 
                      ? 'border-aura-secondary bg-aura-secondary/10' 
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  onClick={() => handleChangeBillingPath('org_funded')}
                  data-testid="org-funded-option"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                        billingPath === 'org_funded' ? 'border-aura-secondary bg-aura-secondary' : 'border-white/30'
                      }`}>
                        {billingPath === 'org_funded' && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <p className="font-heading font-bold text-white uppercase">Organization-Funded</p>
                        <p className="text-sm text-aura-text-muted mt-1">
                          Your organization pays for all athletes. Best for private schools and funded academies.
                        </p>
                      </div>
                    </div>
                    <Badge className="bg-aura-secondary/20 text-aura-secondary border-0">
                      Recommended
                    </Badge>
                  </div>
                  <div className="mt-4 p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-aura-text-muted">Platform Fee</span>
                      <span className="font-bold text-white">$49/month</span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm text-aura-text-muted">Per Athlete</span>
                      <span className="font-bold text-white">$2/month</span>
                    </div>
                    {orgData?.athlete_count > 0 && (
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10">
                        <span className="text-sm text-aura-text-muted">
                          Estimated Total ({orgData.athlete_count} athletes)
                        </span>
                        <span className="font-heading font-bold text-lg text-aura-secondary">
                          ${49 + (orgData.athlete_count * 2)}/mo
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Athlete-Funded Option */}
                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    billingPath === 'athlete_funded' 
                      ? 'border-aura-success bg-aura-success/10' 
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  onClick={() => handleChangeBillingPath('athlete_funded')}
                  data-testid="athlete-funded-option"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                      billingPath === 'athlete_funded' ? 'border-aura-success bg-aura-success' : 'border-white/30'
                    }`}>
                      {billingPath === 'athlete_funded' && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <p className="font-heading font-bold text-white uppercase">Athlete-Funded</p>
                      <p className="text-sm text-aura-text-muted mt-1">
                        Each athlete pays individually. Best for public schools and budget-constrained clubs.
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-aura-text-muted">Organization Cost</span>
                      <span className="font-bold text-aura-success">$0/month</span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm text-aura-text-muted">Athletes Pay</span>
                      <span className="font-bold text-white">$3/month each</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment History Placeholder */}
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  Payment History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <AlertTriangle className="w-10 h-10 text-yellow-400 mx-auto mb-3" />
                  <p className="text-aura-text-muted">
                    Payment integration coming soon.
                  </p>
                  <p className="text-sm text-aura-text-subtle mt-1">
                    Stripe subscription management will be available here.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invite Management */}
          <TabsContent value="invites" className="space-y-6">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
                  Invite Code
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-aura-text-muted">
                  Share this code with athletes to let them join your organization.
                </p>

                <div className="p-6 bg-white/5 rounded-lg text-center">
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-2">
                    Current Invite Code
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <code className="font-mono font-bold text-4xl text-aura-secondary">
                      {orgData?.invite_code}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyInviteCode}
                      className="text-aura-text-muted hover:text-white"
                      data-testid="copy-invite-code-btn"
                    >
                      {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                  <div>
                    <p className="font-medium text-white">Need a new code?</p>
                    <p className="text-sm text-aura-text-muted">
                      Regenerating will invalidate the current code.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleRegenerateInviteCode}
                    disabled={regenerating}
                    className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                    data-testid="regenerate-code-btn"
                  >
                    {regenerating ? (
                      <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Regenerate
                  </Button>
                </div>

                {/* How to Join Instructions */}
                <Card className="bg-aura-bg border-white/10">
                  <CardContent className="p-4">
                    <p className="font-medium text-white mb-3">How Athletes Join</p>
                    <ol className="space-y-2 text-sm text-aura-text-muted">
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-aura-secondary/20 text-aura-secondary flex items-center justify-center text-xs flex-shrink-0">1</span>
                        Athlete creates an account at aura-athlete.com
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-aura-secondary/20 text-aura-secondary flex items-center justify-center text-xs flex-shrink-0">2</span>
                        From their dashboard, they click "Join Your Team"
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-aura-secondary/20 text-aura-secondary flex items-center justify-center text-xs flex-shrink-0">3</span>
                        They enter your invite code: <code className="text-aura-secondary ml-1">{orgData?.invite_code}</code>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-aura-secondary/20 text-aura-secondary flex items-center justify-center text-xs flex-shrink-0">4</span>
                        They're automatically added to your roster!
                      </li>
                    </ol>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default OrganizationSettingsPage;
